import * as CSS from 'csstype'
import {CSSProperties} from 'react'

import {actionButtonHeight, shadowBlurRadiusLevel1} from './constants'

import {FontTheme} from './fonts'
import {StyleValues} from './styles/types'
import {Theme} from './styles/theme'

const mobileWidth = 767

export const modalSectionMarginBottom = '24px'

export const emptyStateImageWidth = '285px'

export const containerBorderRadius = '20px'
export const containerBorderRadiusMedium = '16px'

export const topContainerPaddingTop = shadowBlurRadiusLevel1
// ideally all theme fonts have a line height, but they don't yet, so we need to specify values for proper height calculation right now.
export const gridTitleLineHeight = 24
export const authorTitleLineHeight = 20
export const borderAndShadowContainerPadding = 10
export const metadataContainerTopPadding = 12
export const buttonContainerPaddingTop = 6

export function computeGridNoteContainerHeight(previewWidth: number, paddingTop = 0, paddingBottom = 0) {
  // computedImageHeight: the final height (which we do not define explicitly) that you'd see if you inspect the image element post-render
  const computedImageHeight = previewWidth ? previewWidth - blurRadiusAdjustment : 0

  return (
    topContainerPaddingTop +
    borderAndShadowContainerPadding +
    computedImageHeight +
    metadataContainerTopPadding +
    gridTitleLineHeight +
    authorTitleLineHeight +
    actionButtonHeight +
    noteGridGap +
    paddingTop +
    paddingBottom
  )
}

/**
 * This allows us to maintain the use of these additional keys without
 * opening it up to _literally any_ key as "Partial<Record<>>" does.
 */
export type PartialRecord<K extends keyof any, T> = {
  [P in K]?: T
}

/**
 * Omit only widens the type of T so that it does not contain *known*
 * properties K, which is not the same as prohibiting K.
 * ForceOmit actually prohibits K by making it optional with a value type of
 * never (and undefined, due to a TypeScript issue).
 *
 * source: https://stackoverflow.com/a/53796918
 */
export type ForceOmit<T, K extends keyof T> = Omit<T, K> & Partial<Record<K, never>>

/** Human-friendly designations for resolution breaks. */
export type ResolutionBreak = 'mobile' | 'md' | 'lg' | 'xl' | 'xxl'

export const breakpoints: Record<ResolutionBreak, number> = {
  mobile: 0,
  md: mobileWidth + 1,
  lg: 992,
  xl: 1200,
  xxl: 1400
}

export const centerVertical: StyleValues = {
  position: 'relative',
  top: '50%',
  transform: 'translateY(-50%)'
}

export const centerHorizontal: StyleValues = {
  position: 'relative',
  left: '50%',
  transform: 'translateX(-50%)'
}

export const horizontalButtonContainer: CSSProperties = {
  display: 'flex',
  flexDirection: 'row',
  gap: '8px'
}

export function inputField(theme: Theme): CSSProperties {
  return {
    backgroundColor: theme.core.b1,
    color: theme.core.f1,
    height: '40px',
    margin: '5px 0',
    paddingLeft: '20px',
    fontSize: '15px',
    fontWeight: 300,
    width: '100%',
    border: `1px solid ${theme.core.f4}`,
    borderRadius: '4px'
  }
}

export function boopStyle(color: CSS.Property.Color, size: CSS.Property.Width = '8px'): StyleValues {
  return {
    backgroundColor: color,
    width: size,
    height: size,
    minWidth: '8px',
    minHeight: '8px',
    borderRadius: '100px',
    marginBottom: '10px'
  }
}

export function modalBorderStyle(theme: Theme) {
  return {
    ...theme.elevation.level3,
    borderRadius: containerBorderRadiusMedium
  }
}

export function modalContainerStyle(theme: Theme): StyleValues {
  return {
    ...modalBorderStyle(theme),
    position: 'relative',
    width: '90vw',
    maxWidth: '640px',
    background: theme.core.b1,
    overflow: 'hidden'
  }
}

export const modalContainerStyleMobile = {
  mobile: {
    width: '90vw'
  }
}

export function rgba(r: number, g: number, b: number, a: number) {
  return `rgba(${r}, ${g}, ${b}, ${a})`
}

/**
 * Generate styles for a text input.
 *
 * @param theme Theme to apply to the styles.
 * @returns Style to be applied to a text input.
 */
export function textInputStyle(theme: Theme): CSSProperties {
  return {
    borderRadius: containerBorderRadius,
    color: theme.core.f1,
    backgroundColor: theme.core.b1,
    height: '40px',
    margin: '5px 0',
    paddingLeft: '20px',
    fontSize: '20px',
    border: '1px solid lightgray'
  }
}

/**
 * Generate theme-applied styling for visual representation of tags.
 */
export const tagStyle = (theme: Theme, fontStyles: FontTheme) => ({
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  ...fontStyles.bodyMedium,
  backgroundColor: theme.neutral.container,
  fontWeight: 500,
  padding: '4px 16px',
  marginRight: '6px',
  borderRadius: '100px',
  cursor: 'pointer',
  color: theme.core.f2
})

/**
 * Generate tag style on hover, to be applied over tagStyle
 */
export const tagStyleHover = (theme: Theme) => ({
  backgroundColor: theme.neutral.containerHover
})

export const infoBubble = (backgroundColor: string, borderColor: string): StyleValues => ({
  backgroundColor: backgroundColor,
  borderRadius: containerBorderRadius,
  margin: '20px 0',
  padding: '8px',
  border: `1px solid ${borderColor}`
})

export const tagTextStyle = (maxWidth?: string): StyleValues => ({
  display: 'inline-block',
  textOverflow: 'ellipsis',
  overflow: 'hidden',
  whiteSpace: 'nowrap',

  ...(maxWidth ? {maxWidth} : {})
})

export const infoTag = (foregroundColor: string, backgroundColor: string, outlineColor?: string): StyleValues => ({
  width: 'fit-content',
  fontSize: '12px',
  color: foregroundColor,
  borderRadius: '4px',
  padding: '2px 4px',
  display: 'flex',
  flexDirection: 'row',
  alignContent: 'center',
  backgroundColor: backgroundColor,
  opacity: 0.8,
  outline: outlineColor ? `1px solid ${outlineColor}` : 'inherit'
})

/** Ordered mapping of resolutions and their names. */
const sortedBreakpoints = Object.keys(breakpoints)
  .reduce(
    (acc, val) => {
      const key = val as ResolutionBreak
      acc.push({
        minWidth: breakpoints[key],
        name: key
      })
      return acc
    },
    [] as {minWidth: number; name: ResolutionBreak}[]
  )
  .sort((a, b) => a.minWidth - b.minWidth)

// Handle max width by way of padding to make sure calculations that use paddings work correctly.
// e.g. placement of the drilldown card backing.
//

export const blurRadiusAdjustment = shadowBlurRadiusLevel1 * 2

export const noteGridGap = 24

// // do not change any of the values in the following block without also referring to the same-named values in noteGrid.scss
export const galleryHeaderHeightNum = 72
export const galleryHeaderHeight = `${galleryHeaderHeightNum}px`
export const filterButtonLineHeight = 24
export const filterButtonContainerPaddingBottom = 6
export const searchFieldContainerHeight = 43
export const searchControlRowBottomMargin = 20

export const inlineHeaderStyle = {
  paddingLeft: `${noteGridGap}px`
} as CSSProperties

export const pagePaddingNumbers = {
  paddingTop: 14,
  paddingRight: noteGridGap,
  paddingBottom: 14,
  paddingLeft: noteGridGap,

  mobile: {
    paddingTop: 14,
    paddingRight: noteGridGap,
    paddingBottom: 14,
    paddingLeft: noteGridGap
  }
}

export const pagePadding = {
  paddingTop: `${pagePaddingNumbers.paddingTop}px`,
  paddingRight: `${pagePaddingNumbers.paddingRight}px`,
  paddingBottom: `${pagePaddingNumbers.paddingBottom}px`,
  paddingLeft: `${pagePaddingNumbers.paddingLeft}px`,

  mobile: {
    paddingTop: `${pagePaddingNumbers.mobile.paddingTop}px`,
    paddingRight: `${pagePaddingNumbers.mobile.paddingRight}px`,
    paddingBottom: `${pagePaddingNumbers.mobile.paddingBottom}px`,
    paddingLeft: `${pagePaddingNumbers.mobile.paddingLeft}px`
  }
}

export const pagePaddingMinusShadowBlurRadius = {
  ...pagePadding,
  paddingRight: `calc(${pagePadding.paddingRight} - ${shadowBlurRadiusLevel1}px)`,
  paddingLeft: `calc(${pagePadding.paddingLeft} - ${shadowBlurRadiusLevel1}px)`,
  mobile: {
    ...pagePadding.mobile
  }
}

export const columnsForWindowWidth = (windowWidth: number | undefined) => {
  if (!windowWidth) {
    return 1
  }

  const optimalCellWidth = windowWidth > 1024 ? 416 : 275
  return Math.max(1, Math.round(windowWidth / optimalCellWidth))
}

export const previewWidthForWindowWidth = (windowWidth: number | undefined) => {
  if (!windowWidth) {
    return undefined
  }

  const columns = columnsForWindowWidth(windowWidth)
  const windowEdgePaddingLeftAndRight = noteGridGap * 2
  return (
    (windowWidth - windowEdgePaddingLeftAndRight - noteGridGap * (columns - 1)) / columns +
    (columns === 1 ? 0 : blurRadiusAdjustment)
  )
}

/**
 * Get the resolution break according to the viewport width.
 *
 * @param screenWidth Width of the viewport in pixels.
 * @returns Resolution break ID.
 */
export function getResolutionBreak(screenWidth: number): ResolutionBreak {
  // Determine the index of the resolution being applied.
  let resolutionIndex = 0
  for (let i = 0; i < sortedBreakpoints.length; i++) {
    const resolution = sortedBreakpoints[i]!.minWidth
    if (screenWidth < resolution) {
      break
    } else {
      resolutionIndex = i
    }
  }

  return sortedBreakpoints[resolutionIndex]!.name
}

/**
 * Cap the number of lines that will display, indicating the cutoff with an ellipse.
 *
 * @param lines Maxiumum number of lines of text to display before ellipsing.
 * @returns Style string which will clamp the element to the specified number of lines.
 */
export function lineClampStyle(lines: number): CSSProperties {
  return {
    display: '-webkit-box',
    WebkitLineClamp: lines,
    WebkitBoxOrient: 'vertical',
    overflow: 'hidden'
  }
}

/**
 * Create styles for a 'close' icon on a modal.
 * @param fillColor Color to fill in the 'close' icon.
 * @returns Style values to be applied to a close icon in the corner of a modal.
 */
export function closeIconStyle(fillColor: string) {
  return {
    position: 'absolute' as CSS.Property.Position,
    top: '36px',
    right: '36px',
    cursor: 'pointer',
    fill: fillColor
  }
}
